import { Sprite } from 'pixi.js';
import { Boss, type BossDef } from './boss-base';
import type { PatternRunner } from '@/patterns/emitter';
import { getTexture, makeSprite } from '@/assets/resolver';
import type { PatternDef } from '@/patterns/dsl';
import type { BulletSystem } from '@/entities/bullets';
import type { TelegraphSink } from '@/render/telegraphs';
import { BOSS_FEEDBACK } from '@/config/boss-feedback';
import { CT, CarrierTurrets } from './carrier-turrets';

/**
 * B2 — Crystal Carrier. Weak-point boss: armor blocks ALL damage while the
 * crystal is closed; it opens on a cycle and that's the damage window —
 * which is also when the boss switches to its most dangerous aimed fire
 * (risk/reward). Signature movement: strafe. docs/02-bosses.md
 * Signatures: the spiral wall while closed, the aimed fan while open.
 * The crystal cracks with light before it opens and dims before it shuts;
 * phase 2 unfolds four crystal turrets in the arena corners (docs/02 B4).
 */
export const CARRIER: BossDef = {
  id: 'carrier',
  nameKey: 'boss.carrier',
  tier: 2,
  baseHp: 950,
  radius: 92,
  bodyKey: 'boss/carrier/body',
  movement: 'strafe',
  contactDamage: 1,
  phases: [
    {
      // Phase 1 — closed: lazy double spiral wall
      hpPct: 1.0,
      patterns: [
        {
          type: 'spiral', arms: 2, interval: 0.16, angularSpeed: 55,
          bullet: 'bullet/enemy/shard', speed: 140, radius: 11, damage: 1,
        },
        {
          // Cross-hatch: a sparse counter-spiral turns the wall into a mesh
          type: 'spiral', arms: 2, interval: 0.3, angularSpeed: -38, unlockTier: 1,
          bullet: 'bullet/enemy/ring', speed: 120, radius: 11, damage: 2,
        },
      ],
    },
    {
      // Phase 2 (50%) — spirals reverse and tighten
      hpPct: 0.5,
      speedMul: 1.25,
      patterns: [
        {
          type: 'spiral', arms: 3, interval: 0.14, angularSpeed: -70,
          bullet: 'bullet/enemy/ring', speed: 155, radius: 11, damage: 2,
        },
      ],
    },
  ],
  evolve: [
    {
      // Crystal lance: the hull glows, then one fast heavy shard
      tier: 3,
      pattern: {
        type: 'aimed', count: 1, spreadDeg: 0, interval: 2.9,
        telegraph: 0.5,
        bullet: 'bullet/enemy/ring', speed: 330, radius: 11, damage: 2,
      },
    },
  ],
};

/**
 * Patterns fired only while the crystal is OPEN (per phase index) — tier
 * gated and ramped like every other attack (X6).
 */
const OPEN_PATTERNS: ReadonlyArray<ReadonlyArray<PatternDef>> = [
  [
    {
      type: 'aimed', count: 5, spreadDeg: 36, interval: 1.0,
      telegraph: 0.35,
      bullet: 'bullet/enemy/shard', speed: 290, radius: 10, damage: 1,
    },
    {
      type: 'radial', count: 12, interval: 1.6, spinDeg: 15, unlockTier: 2,
      bullet: 'bullet/enemy/ring', speed: 140, radius: 10, damage: 2,
    },
  ],
  [
    {
      type: 'aimed', count: 5, spreadDeg: 32, interval: 0.9,
      telegraph: 0.3, burst: 2, burstGap: 0.25,
      bullet: 'bullet/enemy/shard', speed: 310, radius: 10, damage: 1,
    },
    {
      type: 'radial', count: 12, interval: 1.4, spinDeg: 15, unlockTier: 1,
      bullet: 'bullet/enemy/ring', speed: 150, radius: 10, damage: 2,
    },
  ],
];

const CYCLE = { closed: 5, open: 3 };
/** Crystal read-outs: light leaks before opening, it dims before shutting */
const CRYSTAL = {
  openWarn: 0.8,
  closeWarn: 0.5,
  pulse: 0.06,
  warnPulse: 0.16,
  dimTint: 0x8a8070,
  chargeTint: 0xffd08a,
} as const;

export class CarrierBoss extends Boss {
  private readonly crystal: Sprite;
  private openTimer = CYCLE.closed;
  private isOpen = false;
  private openRunners: PatternRunner[] = [];
  private readonly turrets: CarrierTurrets;
  private time = 0;
  private lastPx = 0;
  private lastPy = 0;

  constructor(level: number, x: number, y: number, homeX: number, homeY: number) {
    super(CARRIER, level, x, y, homeX, homeY);
    this.crystal = makeSprite('boss/carrier/crystal', 0);
    this.view.addChild(this.crystal);
    this.turrets = new CarrierTurrets(this.view);
  }

  get weakPointOpen(): boolean {
    return this.isOpen;
  }

  override get isVulnerable(): boolean {
    return super.isVulnerable && this.isOpen;
  }

  override update(
    dt: number,
    px: number,
    py: number,
    bullets: BulletSystem,
    telegraphs?: TelegraphSink,
  ): void {
    if (this.dead) return;
    this.time += dt;
    this.lastPx = px;
    this.lastPy = py;

    // Weak point cycle
    this.openTimer -= dt;
    if (this.openTimer <= 0) {
      this.isOpen = !this.isOpen;
      this.openTimer = this.isOpen ? CYCLE.open : CYCLE.closed;
      if (this.isOpen) this.refreshOpenRunners();
    }

    // Base handles phases/movement; closed patterns live in def.phases
    const phaseBefore = this.phaseIndex;
    super.update(dt, px, py, bullets, telegraphs);
    if (this.phaseIndex !== phaseBefore) {
      this.refreshOpenRunners();
      // Phase 2: crystal turrets unfold in the arena corners (B4)
      if (this.phaseIndex >= 1 && !this.turrets.deployed) {
        this.turrets.deploy(this.arenaW, this.arenaH, this.maxHp * CT.hpShare, this.events, px, py);
      }
    }
    this.turrets.update(dt, px, py, bullets, this.intensity, this.bulletSpeedMul);

    // Open-window patterns fire in addition to the (sparser) closed spiral
    if (this.isOpen) {
      for (const r of this.openRunners) {
        r.update(dt, this.x, this.y, px, py, bullets, telegraphs);
      }
    }
  }

  /** Turrets are optional targets: their HP never drains the boss bar. */
  override absorbBullets(bullets: BulletSystem): { hits: number; dealt: number } {
    const res = super.absorbBullets(bullets);
    res.dealt += this.turrets.absorb(bullets, this.turretDown);
    return res;
  }

  override hitByAoe(x: number, y: number, radius: number): boolean {
    return super.hitByAoe(x, y, radius) || this.turrets.nearest(x, y, radius + CT.radius) !== null;
  }

  /** One part per blast (X5): the open hull first, else the turret inside it —
   *  so the explosion number is exactly what that one part lost. */
  override applyAoe(x: number, y: number, radius: number, damage: number): number {
    const hull = super.applyAoe(x, y, radius, damage);
    if (hull > 0) return hull;
    return this.turrets.applyAoe(x, y, radius, damage, this.turretDown, this.aoeAt);
  }

  /** Shut crystal: auto-aim takes the nearest turret instead. */
  override aimPoint(px: number, py: number): { x: number; y: number } {
    if (this.isOpen) return this;
    return this.turrets.nearest(px, py) ?? this;
  }

  private readonly turretDown = (x: number, y: number): void => {
    this.events?.burst(x, y, BOSS_FEEDBACK.carrierTurret);
  };

  /** Armor blocks everything while closed (the "clink" rule). */
  override takeDamage(amount: number): boolean {
    if (!this.isOpen) return false;
    return super.takeDamage(amount);
  }

  private refreshOpenRunners(): void {
    const set = OPEN_PATTERNS[Math.max(0, Math.min(this.phaseIndex, OPEN_PATTERNS.length - 1))]!;
    this.openRunners = this.makeRunners(set);
  }

  protected override get telegraphing(): boolean {
    if (super.telegraphing) return true;
    return this.isOpen && this.openRunners.some((r) => r.warning);
  }

  override render(alpha: number): void {
    super.render(alpha);
    const t = this.openTimer;
    const opening = !this.isOpen && t < CRYSTAL.openWarn;
    const closing = this.isOpen && t < CRYSTAL.closeWarn;
    let frame = this.isOpen ? 1 : 0;
    let pulse = this.isOpen ? CRYSTAL.pulse * Math.sin(this.time * 11) : 0;
    if (opening) {
      // Light cracks through the shutter, flickering faster as it parts
      const p = 1 - t / CRYSTAL.openWarn;
      frame = Math.sin(this.time * (14 + 40 * p)) > 0.2 - p ? 1 : 0;
      pulse = CRYSTAL.warnPulse * p;
    }
    this.crystal.texture = getTexture('boss/carrier/crystal', frame);
    this.crystal.scale.set(1 + pulse);
    // Aimed wind-up glows in the crystal; a closing crystal dims first
    this.crystal.tint = this.telegraphing ? CRYSTAL.chargeTint
      : closing && Math.sin(this.time * 30) > 0 ? CRYSTAL.dimTint : 0xffffff;
    const vx = this.prevX + (this.x - this.prevX) * alpha;
    const vy = this.prevY + (this.y - this.prevY) * alpha;
    this.turrets.render(vx, vy, this.lastPx, this.lastPy, this.intensity);
  }
}
